import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { loggerId, type LoggerId } from './Logger.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.notification_ */
export type NotificationId = string & { __brand: 'NotificationId' }

/** Represents the table public.notification_ */
export default interface NotificationTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<NotificationId, NotificationId | undefined, NotificationId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  message: ColumnType<string, string, string>

  /** Database type: pg_catalog.bool */
  isSent: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.bool */
  isResolved: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.timestamptz */
  resolvedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.uuid */
  resolvedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  messagePriority: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  messageCategory: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  numberRaised: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  loggerId: ColumnType<LoggerId | null, LoggerId | null, LoggerId | null>

  /** Database type: pg_catalog.int4 */
  cableId: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.timestamptz */
  occurredAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >
}

export type Notification = Selectable<NotificationTable>

export type NotificationInitializer = Insertable<NotificationTable>

export type NotificationMutator = Updateable<NotificationTable>

export const notificationId = z.string() as unknown as z.Schema<NotificationId>

export const notification = z.object({
  id: notificationId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  message: z.string(),
  isSent: z.boolean(),
  isResolved: z.boolean(),
  resolvedAt: z.coerce.date().nullable().optional(),
  resolvedBy: userId.nullable().optional(),
  messagePriority: z.string().nullable().optional(),
  messageCategory: z.string().nullable().optional(),
  numberRaised: z.number().nullable().optional(),
  loggerId: loggerId.nullable().optional(),
  cableId: z.number().nullable().optional(),
  occurredAt: z.coerce.date().nullable().optional(),
}) as unknown as z.Schema<Notification>

export const notificationInitializer = z.object({
  id: notificationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  message: z.string(),
  isSent: z.boolean().optional(),
  isResolved: z.boolean().optional(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedBy: userId.optional().nullable(),
  messagePriority: z.string().optional().nullable(),
  messageCategory: z.string().optional().nullable(),
  numberRaised: z.number().optional().nullable(),
  loggerId: loggerId.optional().nullable(),
  cableId: z.number().optional().nullable(),
  occurredAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<NotificationInitializer>

export const notificationMutator = z.object({
  id: notificationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  message: z.string().optional(),
  isSent: z.boolean().optional(),
  isResolved: z.boolean().optional(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedBy: userId.optional().nullable(),
  messagePriority: z.string().optional().nullable(),
  messageCategory: z.string().optional().nullable(),
  numberRaised: z.number().optional().nullable(),
  loggerId: loggerId.optional().nullable(),
  cableId: z.number().optional().nullable(),
  occurredAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<NotificationMutator>
